import type { VoidFunctionComponent } from 'react';
import { Modal } from 'react-bootstrap';
import { Link } from 'react-router';

import Locator from '../../../../shared/Locator';
import { ButtonGroup, DocsLink, icons } from '../../generic';
import Button from '../../generic/Button';
import Tabs from '../../generic/Tabs';
import Text from '../../generic/Text';
import type { ManagePackageLabelsState } from '../../projects/dependenciesV2/hooks/useDependencyModals';
import TabContent from './TabContent';
import { Tab } from './types';
import { usePackageLabelState } from './usePackageLabelState';

type ModalProps = {
  context: ManagePackageLabelsState;
  // The locator of the user revision that imports the dependency.
  // If provided, then an issue scan will be performed after any changes are made.
  parentRevisionLocator?: string;

  // Modal state management
  show: boolean;
  onHide(): void;

  // An optional callback that the caller can provide that will be triggered when a project correction is upserted or
  // deleted. Use this so that your component can trigger a data refresh, etc.
  // onChange?(): void;
};

const TabIcons = {
  [Tab.Revision]: icons.codeCommit,
  [Tab.Project]: icons.locationDot,
  [Tab.Global]: icons.globeOutline,
} as const;

const ModalManagePackageLabels: VoidFunctionComponent<ModalProps> = ({
  onHide,
  show,
  context: { revision, title },
  parentRevisionLocator,
}) => {
  const packageId = Locator.parsePackageString(revision || '');
  const parentProjectLocator = Locator.parsePackageString(parentRevisionLocator || '');
  const state = usePackageLabelState(packageId);

  if (!show || !revision || !parentRevisionLocator) {
    return null;
  }

  const onSave = () => {
    switch (state.tabs.activeTab) {
      case Tab.Global:
        return state.onSaveGlobal();
      case Tab.Project:
        return state.onSaveProject(parentProjectLocator);
      case Tab.Revision:
        return state.onSaveRevision(parentRevisionLocator);
      default:
        // eslint-disable-next-line consistent-return, no-useless-return
        return;
    }
  };

  return (
    <Modal onHide={onHide} animation show={show} backdrop="static" keyboard={false}>
      <Modal.Header closeButton>
        <Modal.Title>Manage Labels</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <span>
          You can create and manage labels in <Link to="/account/settings/organization">Organization Settings</Link>.
          Learn more about using labels in our <DocsLink to="">docs</DocsLink>.
        </span>
        <Text.H3>{title}</Text.H3>
        <Tabs>
          {state.tabs.tabs.map((tab) => (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <Tabs.Tab key={tab.label} {...tab} icon={TabIcons[tab.label]} />
          ))}
        </Tabs>
        <TabContent
          tab={Tab.Global}
          activeTab={state.tabs.activeTab}
          helpText="These labels are applied globally, across all of your projects."
          parentRevisionLocator={parentRevisionLocator}
          state={state}
          labelsSelected={state.globalLabelsSelected}
          setLabelsSelected={state.setGlobalLabelsSelected}
          hasNoPermission={false} // global labels are always visible
        />
        <TabContent
          tab={Tab.Project}
          activeTab={state.tabs.activeTab}
          helpText="These labels are applied only in this project."
          parentRevisionLocator={parentRevisionLocator}
          state={state}
          labelsSelected={state.projectLabelsSelected}
          setLabelsSelected={state.setProjectLabelsSelected}
          hasNoPermission={state.hasNoPermission}
        />
        <TabContent
          tab={Tab.Revision}
          activeTab={state.tabs.activeTab}
          helpText="These labels are only visible in the current revision of this project."
          parentRevisionLocator={parentRevisionLocator}
          state={state}
          labelsSelected={state.revisionLabelsSelected}
          setLabelsSelected={state.setRevisionLabelsSelected}
          hasNoPermission={state.hasNoPermission}
        />
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button type="secondary" size="medium" onClick={onHide}>
            Cancel
          </Button>
          <Button
            type="primary"
            size="medium"
            isDisabled={state.hasNoPermission && state.tabs.activeTab !== Tab.Global}
            onClick={onSave}
          >
            Save
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalManagePackageLabels;
